/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Button, Divider, Breadcrumbs, Tab, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import { useLocation, useParams } from "react-router-dom";
import UsersTable from "./UsersTable";
import SiteSettings from "./SiteSettings";
import UserSettings from "./UserSettings";
import { useNavigate } from "react-router-dom";
import OrganizationSettings from "./OrganizationSettings";
import NewSiteModal from "./NewSiteModal";
import { useQuery } from "@tanstack/react-query";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  getOrganizationUsersListQuery,
  getOrganizationSitesListQuery,
  getOrganizationsListQuery,
} from "../../lib/Queries";

const OrganizationPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const { organization } = location.state || {};
  const { organization_name, site_name } = useParams();
  const navigate = useNavigate();

  // Tab constants for better readability
  const TABS = {
    PROFILE: 0,
    SITES: 1,
    USERS: 2,
  };

  const styles = {
    caption: {
      color: "rgba(0, 0, 0, 0.60)",
      marginBottom: "1rem",
    },
    settingsPanel: {
      width: "100%",
      py: "3rem",
      px: "4rem",
    },
    subtitle: {
      color: "rgba(0, 0, 0, 0.87)",
      fontWeight: "700",
    },
    genericTab: {
      backgroundColor: "#fff",
      border: "1px solid #E1E2E4",
      borderRadius: "5px",
      width: "12rem",
      maxHeight: "500px",
      justifyContent: "center",
      fontSize: 12,
    },
    genericTabLabelContainer: {
      fontSize: 14,
      paddingLeft: "28px",
      width: "100%",
      minWidth: 0,
      display: "flex",
      flexDirection: "row",
    },
    genericTabLabel: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "100%",
      pr: "8px",
      textAlign: "left",
    },
  };

  const [name, setName] = useState(organization?.name || null);
  const [selectedUser, setSelectedUser] = useState(null); // State to manage the selected user
  const [isNewSiteModalOpen, setIsNewSiteModalOpen] = useState(false);

  // Fetch organizations list to get organization data when not available in state
  const { data: organizationsList } = useQuery(getOrganizationsListQuery());

  // Find organization by name if not available in state
  const getOrganizationFromList = () => {
    if (organization) return organization;

    if (organizationsList?.customers && organization_name) {
      const orgNameFormatted = organization_name.replace(/-/g, " ");
      const foundOrg = organizationsList.customers.find(
        (org) => org.name.replace(/ /g, "-") === organization_name || org.name === orgNameFormatted,
      );
      return foundOrg;
    }

    return null;
  };

  const currentOrganization = getOrganizationFromList();
  const currentOrganizationUuid = currentOrganization?.uuid;

  // Update name when currentOrganization changes
  useEffect(() => {
    if (currentOrganization?.name) {
      setName(currentOrganization.name);
    }
  }, [currentOrganization]);

  // Derive tab index from route
  const tabIndex = useMemo(() => {
    const path = location.pathname;
    if (path.includes("/sites")) return TABS.SITES;
    if (path.includes("/users")) return TABS.USERS;
    return TABS.PROFILE;
  }, [location.pathname]);

  const {
    data: usersList,
    isLoading: usersListIsLoading,
    refetch: refetchUsersList,
  } = useQuery(getOrganizationUsersListQuery(currentOrganizationUuid));

  const { data: sitesList, isLoading: sitesListIsLoading } = useQuery({
    ...getOrganizationSitesListQuery(currentOrganizationUuid),
  });

  // Derive selected site from params + sites list
  const selectedSite = useMemo(() => {
    if (!site_name || !sitesList?.sites) return null;
    const actualSite = sitesList.sites.find(
      (site) =>
        site.site_name.replace(/ /g, "-") === site_name ||
        site.site_name === site_name.replace(/-/g, " "),
    );
    if (!actualSite) return null;
    return { ...actualSite, name: actualSite.site_name };
  }, [site_name, sitesList]);

  const sortedSites = useMemo(() => {
    if (!sitesList?.sites) return [];
    // Keep the left "SITES" navigation consistent by sorting alphabetically by site name.
    return [...sitesList.sites].sort((a, b) => {
      const aName = a?.site_name || "";
      const bName = b?.site_name || "";
      return aName.localeCompare(bName, undefined, { sensitivity: "base" });
    });
  }, [sitesList]);

  const userDisplayName = useMemo(() => {
    if (!selectedUser) return "";
    if (selectedUser.first_name || selectedUser.last_name) {
      return `${selectedUser.first_name || ""} ${selectedUser.last_name || ""}`.trim();
    }
    return selectedUser.name || "";
  }, [selectedUser]);

  const hasSecondCrumb = useMemo(() => {
    if (tabIndex === TABS.PROFILE) return true;
    if (tabIndex === TABS.USERS) return true;
    if (tabIndex === TABS.SITES) return !!selectedSite;
    return false;
  }, [tabIndex, selectedSite]);

  const handleUserRowClick = (user) => {
    setSelectedUser(user);
  };

  const handleUpdatedUser = (user) => {
    setSelectedUser(user);
    refetchUsersList();
  };

  const handleModifiedUsers = () => {
    refetchUsersList();
    setSelectedUser(null);
  };

  const handleTabChange = (event, newValue) => {
    // Navigate to the appropriate route based on the selected tab
    const orgName = organization_name || currentOrganization?.name;
    if (orgName) {
      const orgNameFormatted = orgName.replace(/ /g, "-");
      switch (newValue) {
        case TABS.PROFILE:
          navigate(`/organization/${orgNameFormatted}/profile`, {
            state: { organization: currentOrganization },
          });
          break;
        case TABS.SITES:
          navigate(`/organization/${orgNameFormatted}/sites`, {
            state: { organization: currentOrganization },
          });
          break;
        case TABS.USERS:
          navigate(`/organization/${orgNameFormatted}/users`, {
            state: { organization: currentOrganization },
          });
          break;
        default:
          navigate(`/organization/${orgNameFormatted}/profile`, {
            state: { organization: currentOrganization },
          });
      }
    }
  };

  return (
    <>
      <Box sx={theme.classes.pageContainer}>
        <Box sx={theme.classes.page}>
          <Box>
            {/* TODO standardize this title bar into component so styles are not repeated */}
            <Typography sx={{ mb: "1.5rem", fontSize: "24px", fontWeight: 600 }} variant="h5">
              Admin
            </Typography>
            <Divider />
            <Breadcrumbs aria-label="breadcrumb" sx={{ ...styles.caption, mt: "32px" }}>
              <Button
                sx={{ fontSize: 16, textDecoration: "none", color: "primary.main" }}
                onClick={() => navigate("/settings")}
              >
                Organizations
              </Button>
              <Button
                sx={{
                  fontSize: 16,
                  textDecoration: "none",
                  color: "primary.main",
                }}
                onClick={() => {
                  const orgName = organization_name || currentOrganization?.name;
                  if (orgName) {
                    navigate(`/organization/${orgName.replace(/ /g, "-")}/profile`, {
                      state: { organization: currentOrganization },
                    });
                  }
                }}
              >
                {name}
              </Button>
              {tabIndex === TABS.PROFILE && (
                <Button sx={{ fontSize: 16, textDecoration: "none", color: "text.primary" }}>
                  Org Profile
                </Button>
              )}
              {tabIndex === TABS.USERS && (
                <Button
                  sx={{
                    fontSize: 16,
                    textDecoration: "none",
                    color: "primary.main",
                  }}
                  onClick={() => {
                    setSelectedUser(null);
                    const orgName = organization_name || currentOrganization?.name;
                    if (orgName) {
                      navigate(`/organization/${orgName.replace(/ /g, "-")}/users`, {
                        state: { organization: currentOrganization },
                      });
                    }
                  }}
                >
                  Users
                </Button>
              )}
              {tabIndex === TABS.USERS && selectedUser && (
                <Button sx={{ fontSize: 16, textDecoration: "none", color: "text.primary" }}>
                  {userDisplayName}
                </Button>
              )}
              {tabIndex === TABS.SITES && selectedSite && (
                <Button sx={{ fontSize: 16, textDecoration: "none", color: "text.primary" }}>
                  {selectedSite.name}
                </Button>
              )}
            </Breadcrumbs>
            <Box display="flex" flexDirection="row" mt="2rem" width="100%">
              <Box sx={{ mr: "2rem" }}>
                <Box
                  role="tablist"
                  aria-label="Organization Tabs"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    ...styles.genericTab,
                  }}
                  value={tabIndex}
                  onChange={handleTabChange}
                >
                  <Typography
                    variant="subtitle"
                    sx={{
                      margin: "20px 0 0 20px",
                      ...styles.subtitle,
                    }}
                  >
                    ORG
                  </Typography>
                  <Tab
                    label={<Box sx={styles.genericTabLabel}>Profile</Box>}
                    onClick={() => handleTabChange(null, TABS.PROFILE)}
                    selected={tabIndex === TABS.PROFILE}
                    sx={{
                      ...styles.genericTabLabelContainer,
                      borderRight: tabIndex === TABS.PROFILE ? 2 : 0,
                      borderColor: "primary.main",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      opacity: 0.7,
                      color: "text.primary",
                      "&&.Mui-selected": { color: theme.palette.primary.main },
                    }}
                  />
                  <Tab
                    label={<Box sx={styles.genericTabLabel}>Users</Box>}
                    onClick={() => handleTabChange(null, TABS.USERS)}
                    selected={tabIndex === TABS.USERS}
                    sx={{
                      ...styles.genericTabLabelContainer,
                      borderRight: tabIndex === TABS.USERS ? 2 : 0,
                      borderColor: "primary.main",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      mb: 1,
                      opacity: 0.7,
                      color: "text.primary",
                      "&&.Mui-selected": { color: theme.palette.primary.main },
                    }}
                  />
                  <Typography
                    variant="subtitle"
                    sx={{
                      marginLeft: "20px",
                      ...styles.subtitle,
                    }}
                  >
                    SITES
                  </Typography>
                  <Box sx={{ mt: 1, mb: 1, overflowY: "auto" }}>
                    {sitesListIsLoading && (
                      <Tab
                        label="Loading..."
                        disabled
                        sx={{ ...styles.genericTabLabelContainer, justifyContent: "flex-start" }}
                      />
                    )}
                    {!sitesListIsLoading && (!sitesList?.sites || sitesList.sites.length === 0) && (
                      <Tab
                        label="No sites"
                        disabled
                        sx={{ ...styles.genericTabLabelContainer, justifyContent: "flex-start" }}
                      />
                    )}
                    {!sitesListIsLoading &&
                      sortedSites.map((site) => {
                        const orgName = organization_name || currentOrganization?.name || "";
                        const orgNameFormatted = orgName.replace(/ /g, "-");
                        const siteNameFormatted = site.site_name.replace(/ /g, "-");
                        const isSelected = site_name === siteNameFormatted;
                        return (
                          <Tab
                            key={site.uuid || site.site_name}
                            label={
                              site.site_name && site.site_name.length > 16 ? (
                                <Tooltip title={site.site_name} arrow>
                                  <Box sx={styles.genericTabLabel}>{site.site_name}</Box>
                                </Tooltip>
                              ) : (
                                <Box sx={styles.genericTabLabel}>{site.site_name}</Box>
                              )
                            }
                            onClick={() => {
                              navigate(
                                `/organization/${orgNameFormatted}/sites/${siteNameFormatted}`,
                                {
                                  state: {
                                    organization: currentOrganization,
                                    selectedSite: { ...site, name: site.site_name },
                                  },
                                },
                              );
                            }}
                            selected={isSelected}
                            sx={{
                              ...styles.genericTabLabelContainer,
                              borderRight: isSelected ? 2 : 0,
                              borderColor: "primary.main",
                              justifyContent: "flex-start",
                              alignItems: "flex-start",
                              opacity: 0.7,
                              color: "text.primary",
                              "&&.Mui-selected": { color: theme.palette.primary.main },
                            }}
                          />
                        );
                      })}
                  </Box>
                </Box>
                <Box sx={{ mt: 1, mb: 2, px: 2 }}>
                  <Button
                    variant="text"
                    color="primary"
                    fullWidth
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setIsNewSiteModalOpen(true)}
                  >
                    New Site
                  </Button>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                {tabIndex === TABS.PROFILE && (
                  <>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: "1rem" }}>
                      Org Profile
                    </Typography>
                    <OrganizationSettings organization={currentOrganization} />
                  </>
                )}
                {tabIndex === TABS.SITES && (
                  <Box>
                    {selectedSite ? (
                      <SiteSettings
                        key={selectedSite?.uuid || selectedSite?.name}
                        OrganizationUuid={currentOrganizationUuid}
                        site={selectedSite}
                      />
                    ) : (
                      <Box sx={theme.classes.genericPanel}>
                        <Typography variant="subtitle2" sx={styles.subtitle}>
                          Select a site from the left
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                {tabIndex === TABS.USERS && (
                  <Box>
                    {!selectedUser && (
                      <>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: "1rem" }}>
                          Users
                        </Typography>
                        <Box sx={theme.classes.genericPanel}>
                          <Box sx={styles.settingsPanel}>
                            <Box display="flex" flexDirection="column" width="100%">
                              <UsersTable
                                usersList={usersList || { users: [] }}
                                onRowClick={handleUserRowClick}
                                onCreated={() => handleModifiedUsers()}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}
                    {selectedUser && (
                      <>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: "1rem" }}>
                          {userDisplayName}
                        </Typography>
                        <UserSettings
                          user={selectedUser}
                          onSave={handleUpdatedUser}
                          onDelete={() => handleModifiedUsers()}
                        />
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <NewSiteModal
        open={isNewSiteModalOpen}
        onClose={() => setIsNewSiteModalOpen(false)}
        OrganizationUuid={currentOrganizationUuid}
      />
    </>
  );
};

OrganizationPage.propTypes = {
  OrganizationPage: PropTypes.object,
};

export default OrganizationPage;
